import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { PuntosContext } from '../context/PuntosContext';
import VentasAdmin from './VentasAdmin';
import './AdminDashboard.css';

// All 24 Supabase tables
const AVAILABLE_TABLES = [
  { id: 'usuarios', name: 'Usuarios', category: 'Usuarios & Auth' },
  { id: 'productos', name: 'Productos', category: 'Catálogo' },
  { id: 'categorias', name: 'Categorías', category: 'Catálogo' },
  { id: 'proveedores', name: 'Proveedores', category: 'Catálogo' },
  { id: 'tiendas', name: 'Tiendas', category: 'Marketplace' },
  { id: 'ventas', name: 'Ventas', category: 'Transacciones' },
  { id: 'productos_compras', name: 'Compras de Productos', category: 'Transacciones' },
  { id: 'productos_estados', name: 'Estados de Productos', category: 'Transacciones' },
  { id: 'tickets', name: 'Tickets', category: 'Sorteos' },
  { id: 'tickets_compras', name: 'Compras de Tickets', category: 'Sorteos' },
  { id: 'tickets_participantes', name: 'Participantes de Sorteos', category: 'Sorteos' },
  { id: 'tickets_estados', name: 'Estados de Tickets', category: 'Sorteos' },
  { id: 'sorteos', name: 'Sorteos', category: 'Sorteos' },
  { id: 'sorteos_input', name: 'Inputs de Sorteos', category: 'Sorteos' },
  { id: 'estados_sorteo', name: 'Estados de Sorteo', category: 'Sorteos' },
  { id: 'referidos', name: 'Referidos', category: 'Comunidad' },
  { id: 'roles', name: 'Roles', category: 'Sistema' },
  { id: 'cuentas', name: 'Cuentas Bancarias', category: 'Finanzas' },
  { id: 'forma_pago', name: 'Formas de Pago', category: 'Finanzas' },
  { id: 'partidas', name: 'Partidas (Juegos)', category: 'Gamificación' },
  { id: 'apuestas', name: 'Apuestas', category: 'Gamificación' },
  { id: 'montoncitos', name: 'Montoncitos', category: 'Gamificación' },
  { id: 'tickets_montoncitos', name: 'Tickets Montoncitos', category: 'Gamificación' },
  { id: 'juegos', name: 'Juegos', category: 'Gamificación' },
  { id: 'clientes_ia', name: 'Clientes IA', category: 'CRM & IA' },
];

const DEFAULT_MODULES = [
  { id: 'dashboard', nombre: 'Dashboard', icono: 'dashboard', ruta: '/admin?tab=dashboard', tabla_asociada: null, descripcion: 'Resumen de métricas y KPIs globales', orden: 1, activo: true, requiere_superadmin: false },
  { id: 'usuarios', nombre: 'Usuarios', icono: 'group', ruta: '/admin/usuarios', tabla_asociada: 'usuarios', descripcion: 'Gestión de usuarios, roles y estados', orden: 2, activo: true, requiere_superadmin: false },
  { id: 'productos', nombre: 'Productos', icono: 'inventory_2', ruta: '/admin/productos', tabla_asociada: 'productos', descripcion: 'Catálogo, inventario y disponibilidad', orden: 3, activo: true, requiere_superadmin: false },
  { id: 'ventas', nombre: 'Ventas & Pagos', icono: 'point_of_sale', ruta: '/admin/ventas', tabla_asociada: 'ventas', descripcion: 'Ventas y validación de transferencias', orden: 4, activo: true, requiere_superadmin: false },
  { id: 'sorteos', nombre: 'Sorteos & Premios', icono: 'celebration', ruta: '/admin/tablas?t=sorteos', tabla_asociada: 'sorteos', descripcion: 'Sorteos activos y asignación de ganadores', orden: 5, activo: true, requiere_superadmin: false },
  { id: 'tickets', nombre: 'Tickets & Puntos', icono: 'confirmation_number', ruta: '/admin/tablas?t=tickets', tabla_asociada: 'tickets', descripcion: 'Configuración de tickets y puntos', orden: 6, activo: true, requiere_superadmin: false },
  { id: 'referidos', nombre: 'Comunidad Referidos', icono: 'share', ruta: '/admin/tablas?t=referidos', tabla_asociada: 'referidos', descripcion: 'Vínculos de referidos y embajadores', orden: 7, activo: true, requiere_superadmin: false },
  { id: 'cuentas', nombre: 'Cuentas Bancarias', icono: 'account_balance', ruta: '/admin/tablas?t=cuentas', tabla_asociada: 'cuentas', descripcion: 'Cuentas de transferencia y estados', orden: 8, activo: true, requiere_superadmin: false },
  { id: 'tiendas', nombre: 'Tiendas Aliadas', icono: 'store', ruta: '/admin/tablas?t=tiendas', tabla_asociada: 'tiendas', descripcion: 'Marcas y tiendas marketplace', orden: 9, activo: true, requiere_superadmin: false },
  { id: 'tablas', nombre: 'Explorador Tablas (24)', icono: 'table_view', ruta: '/admin/tablas', tabla_asociada: null, descripcion: 'Visualizador universal de todas las 24 tablas', orden: 10, activo: true, requiere_superadmin: false },
  { id: 'permisos', nombre: 'Gestor de Permisos', icono: 'security', ruta: '/admin?tab=permisos', tabla_asociada: 'admin_permisos', descripcion: 'Asignar permisos a administradores, embajadores y staff', orden: 11, activo: true, requiere_superadmin: true }
];

const KNOWN_TABLE_COLUMNS = {
  cuentas: ['rut', 'nombre', 'banco', 'tipo_cuenta', 'numero', 'email', 'estado'],
  sorteos: ['producto', 'key', 'fecha_inicio', 'fecha_termino', 'fecha_sorteo', 'hora_sorteo', 'estado', 'ganador_producto', 'ganador_puntos'],
  tickets: ['ticket', 'puntos', 'precio', 'publico', 'color'],
  tiendas: ['nombre', 'descripcion', 'logo', 'url', 'activo'],
  categorias: ['categoria', 'descripcion'],
  referidos: ['user_id', 'referente_codigo', 'referido_codigo', 'activo'],
  forma_pago: ['nombre', 'descripcion', 'activo']
};

const CHILEAN_BANKS = [
  'Banco Estado',
  'Banco de Chile / Edwards',
  'Banco Santander Chile',
  'Banco BCI / TBanc',
  'Banco Falabella',
  'Scotiabank Chile',
  'Banco Itaú',
  'Banco BICE',
  'Banco Security',
  'Banco Consorcio',
  'Copec Pay',
  'Mercado Pago',
  'MACH',
  'Tenpo',
  'Prepago Los Héroes',
  'Chek Ripley'
];

const AdminDashboard = ({ initialTab = 'dashboard' }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || initialTab;
  const tableParam = searchParams.get('t') || 'usuarios';

  const [activeTab, setActiveTab] = useState(tabParam);
  const [selectedTable, setSelectedTable] = useState(tableParam);

  const { isAdmin, isEmbajador, isStaff, userData } = useContext(PuntosContext);
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [devBypass, setDevBypass] = useState(isDev);
  const [devRoleSimulated, setDevRoleSimulated] = useState(null); // 'admin', 'embajador', 'staff'

  // Dynamic modules loaded from Supabase table: admin_modulos
  const [modulos, setModulos] = useState(DEFAULT_MODULES);
  const [loadingModulos, setLoadingModulos] = useState(true);

  // User permissions loaded from Supabase table: admin_permisos
  const [userPermissions, setUserPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Global KPI Metrics
  const [kpis, setKpis] = useState({
    totalUsuarios: 0,
    totalProductos: 0,
    totalVentas: 0,
    totalSorteos: 0,
    totalReferidos: 0,
    totalTiendas: 0,
  });
  const [loadingKpis, setLoadingKpis] = useState(true);

  // Tab: Usuarios State
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [usuariosSearch, setUsuariosSearch] = useState('');
  const [usuariosRoleFilter, setUsuariosRoleFilter] = useState('todos');

  // Tab: Productos State
  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [productosSearch, setProductosSearch] = useState('');
  const [productosDisponibilidad, setProductosDisponibilidad] = useState('todos');
  const [updatingSku, setUpdatingSku] = useState(null);

  // Tab: General Tables State
  const [tableData, setTableData] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(25);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedRowDetail, setSelectedRowDetail] = useState(null);

  // CRUD Modals and Clipboard State
  const [copiedId, setCopiedId] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEditingRow, setCurrentEditingRow] = useState(null);
  const [recordFormData, setRecordFormData] = useState({});
  const [savingRecord, setSavingRecord] = useState(false);

  const copyToClipboard = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  // Tab: Permissions Manager State (Admin only)
  const [permSelectedUser, setPermSelectedUser] = useState(null);
  const [permEditRole, setPermEditRole] = useState('embajador');
  const [permMatrix, setPermMatrix] = useState({});
  const [permSaving, setPermSaving] = useState(false);
  const [permSuccessMsg, setPermSuccessMsg] = useState('');
  const [permSearch, setPermSearch] = useState('');

  // Mobile sidebar drawer state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Effective role check
  const currentRole = devRoleSimulated || (userData?.role || (isAdmin ? 'admin' : isEmbajador ? 'embajador' : isStaff ? 'staff' : 'usuario'));
  const isSuperAdmin = currentRole === 'admin' || (devBypass && !devRoleSimulated);

  // Change tab and sync with URL
  const changeTab = (tab, specificTable = null) => {
    setActiveTab(tab);
    const params = { tab };
    if (specificTable) {
      setSelectedTable(specificTable);
      params.t = specificTable;
    }
    setSearchParams(params);
    setSidebarOpen(false);
  };

  // 1. Fetch Dynamic Modules from table 'admin_modulos'
  const fetchModulos = useCallback(async () => {
    setLoadingModulos(true);
    try {
      const { data, error } = await supabase
        .from('admin_modulos')
        .select('*')
        .eq('activo', true)
        .order('orden', { ascending: true });

      if (!error && data && data.length > 0) {
        setModulos(data);
      } else {
        setModulos(DEFAULT_MODULES);
      }
    } catch (err) {
      console.warn('Usando modulos por defecto:', err);
      setModulos(DEFAULT_MODULES);
    } finally {
      setLoadingModulos(false);
    }
  }, []);

  // 2. Fetch User Permissions from table 'admin_permisos'
  const fetchUserPermissions = useCallback(async () => {
    setLoadingPermissions(true);
    try {
      if (isSuperAdmin) {
        // Superadmin has full access to every module
        const fullPerms = {};
        modulos.forEach(m => {
          fullPerms[m.id] = { puede_ver: true, puede_editar: true };
        });
        setUserPermissions(fullPerms);
        setLoadingPermissions(false);
        return;
      }

      const userCode = userData?.codigo_referido;
      if (!userCode) {
        // Fallback permissions based on role
        if (currentRole === 'embajador') {
          setUserPermissions({
            dashboard: { puede_ver: true, puede_editar: false },
            productos: { puede_ver: true, puede_editar: false },
            sorteos: { puede_ver: true, puede_editar: false },
            tickets: { puede_ver: true, puede_editar: false },
            referidos: { puede_ver: true, puede_editar: false },
          });
        } else if (currentRole === 'staff') {
          setUserPermissions({
            dashboard: { puede_ver: true, puede_editar: false },
            productos: { puede_ver: true, puede_editar: true },
            tablas: { puede_ver: true, puede_editar: false },
          });
        }
        setLoadingPermissions(false);
        return;
      }

      const { data, error } = await supabase
        .from('admin_permisos')
        .select('*')
        .eq('user_codigo', userCode);

      if (!error && data && data.length > 0) {
        const permMap = {};
        data.forEach(p => {
          permMap[p.modulo_id] = {
            puede_ver: p.puede_ver,
            puede_editar: p.puede_editar
          };
        });
        setUserPermissions(permMap);
      } else {
        // Defaults if no explicit rows exist yet
        if (currentRole === 'embajador') {
          setUserPermissions({
            dashboard: { puede_ver: true, puede_editar: false },
            productos: { puede_ver: true, puede_editar: false },
            sorteos: { puede_ver: true, puede_editar: false },
            tickets: { puede_ver: true, puede_editar: false },
            referidos: { puede_ver: true, puede_editar: false },
          });
        } else if (currentRole === 'staff') {
          setUserPermissions({
            dashboard: { puede_ver: true, puede_editar: false },
            productos: { puede_ver: true, puede_editar: true },
            tablas: { puede_ver: true, puede_editar: false },
          });
        }
      }
    } catch (err) {
      console.error('Error fetching user permissions:', err);
    } finally {
      setLoadingPermissions(false);
    }
  }, [isSuperAdmin, modulos, userData?.codigo_referido, currentRole]);

  // Check if current user can view a specific module
  const canViewModule = useCallback((moduleId) => {
    if (isSuperAdmin) return true;
    if (moduleId === 'permisos') return false; // Only superadmin can manage permissions
    return !!userPermissions[moduleId]?.puede_ver;
  }, [isSuperAdmin, userPermissions]);

  // Check if current user can edit a specific module
  const canEditModule = useCallback((moduleId) => {
    if (isSuperAdmin) return true;
    return !!userPermissions[moduleId]?.puede_editar;
  }, [isSuperAdmin, userPermissions]);

  // 3. Fetch Global KPIs
  const fetchKpis = useCallback(async () => {
    setLoadingKpis(true);
    try {
      const [
        { count: uCount },
        { count: pCount },
        { count: vCount },
        { count: sCount },
        { count: rCount },
        { count: tCount },
      ] = await Promise.all([
        supabase.from('usuarios').select('*', { count: 'exact', head: true }),
        supabase.from('productos').select('*', { count: 'exact', head: true }),
        supabase.from('ventas').select('*', { count: 'exact', head: true }),
        supabase.from('sorteos').select('*', { count: 'exact', head: true }),
        supabase.from('referidos').select('*', { count: 'exact', head: true }),
        supabase.from('tiendas').select('*', { count: 'exact', head: true }),
      ]);

      setKpis({
        totalUsuarios: uCount || 0,
        totalProductos: pCount || 0,
        totalVentas: vCount || 0,
        totalSorteos: sCount || 0,
        totalReferidos: rCount || 0,
        totalTiendas: tCount || 0,
      });
    } catch (err) {
      console.error('Error fetching admin KPIs:', err);
    } finally {
      setLoadingKpis(false);
    }
  }, []);

  // 4. Fetch Usuarios
  const fetchUsuarios = useCallback(async () => {
    setLoadingUsuarios(true);
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('*')
        .order('id', { ascending: false });

      if (error) throw error;
      setUsuarios(data || []);
    } catch (err) {
      console.error('Error fetching usuarios:', err);
    } finally {
      setLoadingUsuarios(false);
    }
  }, []);

  // 5. Fetch Productos
  const fetchProductos = useCallback(async () => {
    setLoadingProductos(true);
    try {
      const { data, error } = await supabase
        .from('productos')
        .select(`
          *,
          categoria:categorias!productos_categoria_id_fkey (categoria)
        `)
        .order('id', { ascending: false });

      if (error) throw error;
      setProductos(data || []);
    } catch (err) {
      console.error('Error fetching productos:', err);
    } finally {
      setLoadingProductos(false);
    }
  }, []);

  // 6. Fetch Table Data for General Explorer
  const fetchTableData = useCallback(async (tableName) => {
    setLoadingTable(true);
    setTableSearch('');
    setTablePage(1);
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .limit(300);

      if (error) throw error;
      setTableData(data || []);
    } catch (err) {
      console.error(`Error fetching table ${tableName}:`, err);
      setTableData([]);
    } finally {
      setLoadingTable(false);
    }
  }, []);

  // On Mount & Tab change
  useEffect(() => {
    fetchModulos();
    fetchKpis();
  }, [fetchModulos, fetchKpis]);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  useEffect(() => {
    if (activeTab === 'usuarios' || activeTab === 'permisos') {
      fetchUsuarios();
    } else if (activeTab === 'productos') {
      fetchProductos();
    } else if (activeTab === 'tablas') {
      fetchTableData(selectedTable);
    } else if (['sorteos', 'tickets', 'referidos', 'cuentas', 'tiendas'].includes(activeTab)) {
      // Direct shortcut to specific table view
      fetchTableData(activeTab);
    }
  }, [activeTab, fetchUsuarios, fetchProductos, fetchTableData, selectedTable]);

  // Handle User Selection in Permissions Manager
  const handleSelectUserForPerms = async (user) => {
    setPermSelectedUser(user);
    setPermEditRole(user.role || 'embajador');
    setPermSuccessMsg('');

    // Fetch existing permissions for this user from admin_permisos
    try {
      const { data, error } = await supabase
        .from('admin_permisos')
        .select('*')
        .eq('user_codigo', user.codigo_referido);

      const matrix = {};
      modulos.forEach(m => {
        matrix[m.id] = { puede_ver: false, puede_editar: false };
      });

      if (!error && data && data.length > 0) {
        data.forEach(p => {
          matrix[p.modulo_id] = {
            puede_ver: p.puede_ver,
            puede_editar: p.puede_editar
          };
        });
      } else {
        // If no permissions yet, pre-fill based on role
        if (user.role === 'admin') {
          modulos.forEach(m => {
            matrix[m.id] = { puede_ver: true, puede_editar: true };
          });
        } else if (user.role === 'embajador') {
          ['dashboard', 'productos', 'sorteos', 'tickets', 'referidos'].forEach(id => {
            if (matrix[id]) matrix[id] = { puede_ver: true, puede_editar: false };
          });
        } else if (user.role === 'staff') {
          ['dashboard', 'productos', 'tablas'].forEach(id => {
            if (matrix[id]) matrix[id] = { puede_ver: true, puede_editar: true };
          });
        }
      }

      setPermMatrix(matrix);
    } catch (err) {
      console.error('Error fetching permissions for user:', err);
    }
  };

  // Quick Preset Helper
  const applyPermissionPreset = (presetType) => {
    const newMatrix = {};
    modulos.forEach(m => {
      newMatrix[m.id] = { puede_ver: false, puede_editar: false };
    });

    if (presetType === 'admin') {
      setPermEditRole('admin');
      modulos.forEach(m => {
        newMatrix[m.id] = { puede_ver: true, puede_editar: true };
      });
    } else if (presetType === 'embajador') {
      setPermEditRole('embajador');
      ['dashboard', 'productos', 'sorteos', 'tickets', 'referidos'].forEach(id => {
        if (newMatrix[id]) newMatrix[id] = { puede_ver: true, puede_editar: false };
      });
    } else if (presetType === 'staff') {
      setPermEditRole('staff');
      ['dashboard', 'productos', 'tablas'].forEach(id => {
        if (newMatrix[id]) newMatrix[id] = { puede_ver: true, puede_editar: true };
      });
    }

    setPermMatrix(newMatrix);
  };

  // Save Permissions to Supabase: admin_permisos & usuarios
  const handleSavePermissions = async () => {
    if (!permSelectedUser) return;
    setPermSaving(true);
    setPermSuccessMsg('');

    try {
      // 1. Update user role in 'usuarios'
      const { error: userError } = await supabase
        .from('usuarios')
        .update({ role: permEditRole })
        .eq('id', permSelectedUser.id);

      if (userError) throw userError;

      // 2. Prepare upsert rows for 'admin_permisos'
      const upsertRows = Object.keys(permMatrix).map(modId => ({
        user_codigo: permSelectedUser.codigo_referido,
        modulo_id: modId,
        puede_ver: permMatrix[modId].puede_ver,
        puede_editar: permMatrix[modId].puede_editar,
        otorgado_por: userData?.codigo_referido || 'admin',
        updated_at: new Date().toISOString()
      }));

      const { error: permsError } = await supabase
        .from('admin_permisos')
        .upsert(upsertRows, { onConflict: 'user_codigo,modulo_id' });

      if (permsError) throw permsError;

      // Update local state
      setUsuarios(prev => prev.map(u => u.id === permSelectedUser.id ? { ...u, role: permEditRole } : u));
      setPermSelectedUser(prev => ({ ...prev, role: permEditRole }));
      setPermSuccessMsg(`¡Permisos y rol '${permEditRole}' actualizados exitosamente en Supabase!`);
    } catch (err) {
      alert(`Error al guardar permisos: ${err.message}`);
    } finally {
      setPermSaving(false);
    }
  };

  // Toggle user role quick button
  const handleToggleUserRole = async (user, newRole) => {
    const confirmChange = window.confirm(
      `¿Deseas cambiar el rol de ${user.username || user.email} a '${newRole}'?`
    );
    if (!confirmChange) return;

    try {
      const { error } = await supabase
        .from('usuarios')
        .update({ role: newRole })
        .eq('id', user.id);

      if (error) throw error;
      setUsuarios(prev => prev.map(u => u.id === user.id ? { ...u, role: newRole } : u));
    } catch (err) {
      alert(`Error al actualizar rol: ${err.message}`);
    }
  };

  // Toggle Product Availability
  const handleToggleDisponibilidad = async (producto) => {
    if (!canEditModule('productos')) {
      alert('No tienes permisos de edición en el catálogo de productos.');
      return;
    }

    const newStatus = !producto.disponible;
    setUpdatingSku(producto.sku);
    try {
      const { error } = await supabase
        .from('productos')
        .update({ disponible: newStatus })
        .eq('sku', producto.sku);

      if (error) throw error;
      setProductos((prev) =>
        prev.map((p) => (p.sku === producto.sku ? { ...p, disponible: newStatus } : p))
      );
    } catch (err) {
      alert(`Error al cambiar disponibilidad: ${err.message}`);
    } finally {
      setUpdatingSku(null);
    }
  };

  // Filtered Usuarios
  const filteredUsuarios = useMemo(() => {
    return usuarios.filter((u) => {
      const q = usuariosSearch.toLowerCase();
      const matchSearch =
        !q ||
        (u.username && u.username.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.rut && u.rut.toLowerCase().includes(q)) ||
        (u.codigo_referido && u.codigo_referido.toLowerCase().includes(q));

      const matchRole =
        usuariosRoleFilter === 'todos' ||
        u.role === usuariosRoleFilter ||
        (usuariosRoleFilter === 'sin_rol' && !u.role);

      return matchSearch && matchRole;
    });
  }, [usuarios, usuariosSearch, usuariosRoleFilter]);

  // Filtered Productos
  const filteredProductos = useMemo(() => {
    return productos.filter((p) => {
      const q = productosSearch.toLowerCase();
      const matchSearch =
        !q ||
        (p.producto && p.producto.toLowerCase().includes(q)) ||
        (p.sku && p.sku.toLowerCase().includes(q)) ||
        (p.tienda_slug && p.tienda_slug.toLowerCase().includes(q));

      const matchDisp =
        productosDisponibilidad === 'todos' ||
        (productosDisponibilidad === 'disponible' && p.disponible) ||
        (productosDisponibilidad === 'agotado' && !p.disponible);

      return matchSearch && matchDisp;
    });
  }, [productos, productosSearch, productosDisponibilidad]);

  // Processed General Table Data
  const processedTableData = useMemo(() => {
    let result = [...tableData];
    if (tableSearch.trim()) {
      const q = tableSearch.toLowerCase();
      result = result.filter((row) =>
        Object.values(row).some(
          (val) => val !== null && val !== undefined && String(val).toLowerCase().includes(q)
        )
      );
    }

    if (sortColumn) {
      result.sort((a, b) => {
        const valA = a[sortColumn];
        const valB = b[sortColumn];
        if (valA === valB) return 0;
        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;
        return sortDirection === 'asc' ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
      });
    }

    return result;
  }, [tableData, tableSearch, sortColumn, sortDirection]);

  const paginatedTableData = useMemo(() => {
    const startIndex = (tablePage - 1) * tablePageSize;
    return processedTableData.slice(startIndex, startIndex + tablePageSize);
  }, [processedTableData, tablePage, tablePageSize]);

  const totalTablePages = Math.ceil(processedTableData.length / tablePageSize) || 1;

  const tableColumns = useMemo(() => {
    if (tableData.length > 0) {
      return Object.keys(tableData[0]);
    }
    return KNOWN_TABLE_COLUMNS[selectedTable] || ['id', 'created_at', 'nombre', 'estado'];
  }, [tableData, selectedTable]);

  const canEditCurrentTable = isSuperAdmin || canEditModule(selectedTable) || canEditModule('tablas');

  // Open Add Modal
  const handleOpenAddModal = () => {
    const initialForm = {};
    const cols = tableColumns.filter(c => c !== 'id' && c !== 'created_at');
    cols.forEach(c => {
      if (['estado', 'activo', 'publico', 'disponible'].includes(c)) {
        initialForm[c] = true;
      } else {
        initialForm[c] = '';
      }
    });
    setRecordFormData(initialForm);
    setIsAddModalOpen(true);
  };

  // Save New Record
  const handleSaveNewRecord = async (e) => {
    e.preventDefault();
    setSavingRecord(true);
    try {
      const payload = { ...recordFormData };
      delete payload.id;
      delete payload.created_at;

      Object.keys(payload).forEach(key => {
        if (payload[key] === 'true') payload[key] = true;
        if (payload[key] === 'false') payload[key] = false;
        if (['precio', 'puntos', 'precio_cliente', 'precio_proveedor', 'numero'].includes(key) && payload[key] !== '') {
          if (!isNaN(payload[key])) payload[key] = Number(payload[key]);
        }
      });

      const { data, error } = await supabase
        .from(selectedTable)
        .insert([payload])
        .select();

      if (error) throw error;

      alert(`¡Registro guardado exitosamente en ${selectedTable}!`);
      setIsAddModalOpen(false);
      setRecordFormData({});
      fetchTableData(selectedTable);
    } catch (err) {
      alert(`Error al guardar en ${selectedTable}: ${err.message}`);
    } finally {
      setSavingRecord(false);
    }
  };

  // Open Edit Modal
  const handleOpenEditModal = (row) => {
    setCurrentEditingRow(row);
    setRecordFormData({ ...row });
    setIsEditModalOpen(true);
  };

  // Save Edit Record
  const handleSaveEditRecord = async (e) => {
    e.preventDefault();
    if (!currentEditingRow) return;
    setSavingRecord(true);
    try {
      const payload = { ...recordFormData };
      delete payload.id;
      delete payload.created_at;

      Object.keys(payload).forEach(key => {
        if (payload[key] === 'true') payload[key] = true;
        if (payload[key] === 'false') payload[key] = false;
        if (['precio', 'puntos', 'precio_cliente', 'precio_proveedor', 'numero'].includes(key) && payload[key] !== '') {
          if (!isNaN(payload[key])) payload[key] = Number(payload[key]);
        }
      });

      const idKey = currentEditingRow.id !== undefined ? 'id' : Object.keys(currentEditingRow)[0];
      const idVal = currentEditingRow[idKey];

      const { error } = await supabase
        .from(selectedTable)
        .update(payload)
        .eq(idKey, idVal);

      if (error) throw error;

      alert(`¡Registro #${idVal} actualizado con éxito en ${selectedTable}!`);
      setIsEditModalOpen(false);
      setCurrentEditingRow(null);
      setRecordFormData({});
      fetchTableData(selectedTable);
    } catch (err) {
      alert(`Error al actualizar en ${selectedTable}: ${err.message}`);
    } finally {
      setSavingRecord(false);
    }
  };

  // Delete Record
  const handleDeleteRecord = async (row) => {
    const idKey = row.id !== undefined ? 'id' : Object.keys(row)[0];
    const idVal = row[idKey];

    const confirmMsg = `¿Estás seguro de que deseas eliminar este registro (${idKey}: ${idVal}) de la tabla "${selectedTable}"? Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const { error } = await supabase
        .from(selectedTable)
        .delete()
        .eq(idKey, idVal);

      if (error) throw error;

      alert(`Registro #${idVal} eliminado correctamente de ${selectedTable}.`);
      fetchTableData(selectedTable);
    } catch (err) {
      alert(`Error al eliminar registro: ${err.message}`);
    }
  };

  // Export to CSV & JSON
  const exportToCSV = () => {
    if (tableData.length === 0) return;
    const headers = Object.keys(tableData[0]);
    const csvRows = [headers.join(',')];

    for (const row of processedTableData) {
      const values = headers.map((header) => {
        const val = row[header];
        if (val === null || val === undefined) return '';
        const escaped = ('' + val).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedTable}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const exportToJSON = () => {
    if (tableData.length === 0) return;
    const blob = new Blob([JSON.stringify(processedTableData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedTable}_${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
  };

  // Auth gate check
  const isAuthorized = isAdmin || isEmbajador || isStaff || devBypass;

  if (!isAuthorized) {
    return (
      <div className="admin-access-denied-container">
        <div className="admin-access-denied-card">
          <span className="material-symbols-outlined text-red-400 text-6xl mb-4">
            lock
          </span>
          <h2 className="text-2xl font-bold text-white mb-2">Panel Administrativo Protegido</h2>
          <p className="text-gray-300 text-sm max-w-md mx-auto mb-6">
            Esta sección es exclusiva para <strong>Administradores</strong> y <strong>Embajadores</strong> autorizados en MatiMarket.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/ingresar" className="btn btn-primary text-black font-semibold">
              Iniciar Sesión
            </Link>
            <button
              type="button"
              className="btn btn-outline border-gray-600 text-gray-300 hover:bg-gray-800"
              onClick={() => setDevBypass(true)}
            >
              Acceso Modo Desarrollo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Filter modules visible in the sidebar for the current user
  const visibleSidebarModules = modulos.filter((m) => canViewModule(m.id));

  // Determine if active tab is accessible
  const isCurrentTabAllowed = canViewModule(activeTab) || activeTab === 'tablas';

  return (
    <div className="admin-layout-root">
      {/* INTERNAL ADMIN SIDEBAR */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="flex items-center gap-2.5">
            <div className="admin-shield-icon">
              <span className="material-symbols-outlined text-xl">admin_panel_settings</span>
            </div>
            <div>
              <span className="text-sm font-bold text-white block leading-tight">
                MatiMarket
              </span>
              <span className="text-[11px] text-[#b7f900] font-semibold tracking-wider uppercase">
                Admin & Staff
              </span>
            </div>
          </div>
          <button
            type="button"
            className="md:hidden text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Current User Role Card */}
        <div className="admin-sidebar-user-card">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 rounded-full bg-[#b7f900]/20 text-[#b7f900] font-bold text-xs flex items-center justify-center">
              {(userData?.username || 'A')[0].toUpperCase()}
            </div>
            <span className="text-xs font-semibold text-white truncate max-w-[130px]">
              {userData?.username || userData?.email || 'Matias Admin'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className={`role-pill-badge role-${currentRole}`}>
              {currentRole === 'admin' ? '👑 Admin' : currentRole === 'embajador' ? '🌟 Embajador' : currentRole === 'staff' ? '💻 Staff Dev' : currentRole}
            </span>
            {isDev && (
              <span className="text-[10px] text-gray-500 font-mono">Dev Mode</span>
            )}
          </div>

          {/* Dev Role Simulator Switcher */}
          {isDev && (
            <div className="mt-2.5 pt-2 border-t border-gray-800">
              <label className="text-[10px] text-gray-400 block mb-1">Simular Vista Como:</label>
              <select
                value={devRoleSimulated || currentRole}
                onChange={(e) => {
                  setDevRoleSimulated(e.target.value);
                  fetchUserPermissions();
                }}
                className="w-full bg-gray-900 border border-gray-700 text-gray-300 text-[11px] rounded p-1"
              >
                <option value="admin">Super Admin (Acceso Total)</option>
                <option value="embajador">Embajador (Módulos Asignados)</option>
                <option value="staff">Staff Programador (Catálogo/Tablas)</option>
              </select>
            </div>
          )}
        </div>

        {/* Dynamic Sidebar Navigation Menu */}
        <div className="admin-sidebar-menu">
          <span className="sidebar-section-title">Páginas & Módulos</span>
          <nav className="flex flex-col gap-1">
            {visibleSidebarModules.map((modulo) => {
              const isActive = activeTab === modulo.id;
              return (
                <button
                  key={modulo.id}
                  type="button"
                  className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => changeTab(modulo.id, modulo.tabla_asociada)}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="material-symbols-outlined text-lg sidebar-nav-icon">
                      {modulo.icono}
                    </span>
                    <span className="sidebar-nav-label truncate">{modulo.nombre}</span>
                  </div>

                  {/* Associated Table Pill (MCP connection indicator) */}
                  {modulo.tabla_asociada && (
                    <span className="sidebar-table-tag" title={`Tabla vinculada en Supabase: ${modulo.tabla_asociada}`}>
                      {modulo.tabla_asociada}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="admin-sidebar-footer">
          <Link to="/" className="sidebar-back-home">
            <span className="material-symbols-outlined text-sm">arrow_back</span>
            <span>Volver a MatiMarket</span>
          </Link>
        </div>
      </aside>

      {/* MAIN ADMIN WORKSPACE */}
      <main className="admin-main-workspace">
        {/* Workspace Topbar */}
        <header className="admin-workspace-topbar">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="md:hidden text-gray-300 hover:text-white p-1"
              onClick={() => setSidebarOpen(true)}
              aria-label="Abrir menú"
            >
              <span className="material-symbols-outlined text-2xl">menu</span>
            </button>

            <div>
              <h1 className="text-lg font-bold text-white flex items-center gap-2">
                {modulos.find((m) => m.id === activeTab)?.nombre || 'Panel de Administración'}
                {modulos.find((m) => m.id === activeTab)?.tabla_asociada && (
                  <span className="text-xs bg-[#b7f900]/15 border border-[#b7f900]/30 text-[#b7f900] px-2 py-0.5 rounded-full font-mono">
                    tabla: {modulos.find((m) => m.id === activeTab)?.tabla_asociada}
                  </span>
                )}
              </h1>
              <p className="text-xs text-gray-400 hidden sm:block">
                {modulos.find((m) => m.id === activeTab)?.descripcion || 'Administración oficial'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-admin-refresh"
              onClick={() => {
                fetchKpis();
                if (activeTab === 'usuarios') fetchUsuarios();
                if (activeTab === 'productos') fetchProductos();
                if (activeTab === 'tablas' || ['sorteos', 'tickets', 'referidos', 'cuentas', 'tiendas'].includes(activeTab)) {
                  fetchTableData(selectedTable);
                }
              }}
              title="Refrescar datos en vivo"
            >
              <span className="material-symbols-outlined text-sm">refresh</span>
              <span className="text-xs hidden md:inline">Actualizar</span>
            </button>
          </div>
        </header>

        {/* ACCESS CHECK GATE FOR SPECIFIC TAB */}
        {!isCurrentTabAllowed ? (
          <div className="admin-module-locked-view">
            <span className="material-symbols-outlined text-amber-400 text-5xl mb-2">
              lock_person
            </span>
            <h2 className="text-xl font-bold text-white">Módulo Restringido</h2>
            <p className="text-sm text-gray-300 max-w-md mx-auto mt-1 mb-4">
              Tu cuenta ({currentRole}) no cuenta con permisos asignados para visualizar este módulo.
              Ponte en contacto con el Super Administrador para habilitar esta página en el Gestor de Permisos.
            </p>
            <button
              type="button"
              className="btn btn-sm btn-primary text-black font-semibold"
              onClick={() => changeTab('dashboard')}
            >
              Ir a Dashboard
            </button>
          </div>
        ) : (
          <div className="admin-tab-content-area">
            {/* VIEW 1: DASHBOARD */}
            {activeTab === 'dashboard' && (
              <div className="admin-content-view">
                <div className="admin-kpi-grid">
                  <div className="admin-kpi-card" onClick={() => canViewModule('usuarios') && changeTab('usuarios')}>
                    <div className="kpi-icon-box bg-blue-500/15 text-blue-400">
                      <span className="material-symbols-outlined">person</span>
                    </div>
                    <div className="kpi-info">
                      <span className="kpi-label">Usuarios Registrados</span>
                      <span className="kpi-value text-blue-400">
                        {loadingKpis ? '...' : kpis.totalUsuarios}
                      </span>
                      <span className="kpi-subtext">tabla: usuarios</span>
                    </div>
                  </div>

                  <div className="admin-kpi-card" onClick={() => canViewModule('productos') && changeTab('productos')}>
                    <div className="kpi-icon-box bg-[#b7f900]/15 text-[#b7f900]">
                      <span className="material-symbols-outlined">inventory</span>
                    </div>
                    <div className="kpi-info">
                      <span className="kpi-label">Productos en Catálogo</span>
                      <span className="kpi-value text-[#b7f900]">
                        {loadingKpis ? '...' : kpis.totalProductos}
                      </span>
                      <span className="kpi-subtext">tabla: productos</span>
                    </div>
                  </div>

                  <div className="admin-kpi-card" onClick={() => canViewModule('ventas') && changeTab('ventas')}>
                    <div className="kpi-icon-box bg-[#D4AF37]/15 text-[#D4AF37]">
                      <span className="material-symbols-outlined">paid</span>
                    </div>
                    <div className="kpi-info">
                      <span className="kpi-label">Ventas Registradas</span>
                      <span className="kpi-value text-[#D4AF37]">
                        {loadingKpis ? '...' : kpis.totalVentas}
                      </span>
                      <span className="kpi-subtext">tabla: ventas</span>
                    </div>
                  </div>

                  <div className="admin-kpi-card" onClick={() => canViewModule('sorteos') && changeTab('sorteos', 'sorteos')}>
                    <div className="kpi-icon-box bg-purple-500/15 text-purple-400">
                      <span className="material-symbols-outlined">celebration</span>
                    </div>
                    <div className="kpi-info">
                      <span className="kpi-label">Sorteos Creados</span>
                      <span className="kpi-value text-purple-400">
                        {loadingKpis ? '...' : kpis.totalSorteos}
                      </span>
                      <span className="kpi-subtext">tabla: sorteos</span>
                    </div>
                  </div>

                  <div className="admin-kpi-card" onClick={() => canViewModule('referidos') && changeTab('referidos', 'referidos')}>
                    <div className="kpi-icon-box bg-emerald-500/15 text-emerald-400">
                      <span className="material-symbols-outlined">share</span>
                    </div>
                    <div className="kpi-info">
                      <span className="kpi-label">Vínculos de Referidos</span>
                      <span className="kpi-value text-emerald-400">
                        {loadingKpis ? '...' : kpis.totalReferidos}
                      </span>
                      <span className="kpi-subtext">tabla: referidos</span>
                    </div>
                  </div>

                  <div className="admin-kpi-card" onClick={() => canViewModule('tiendas') && changeTab('tiendas', 'tiendas')}>
                    <div className="kpi-icon-box bg-amber-500/15 text-amber-400">
                      <span className="material-symbols-outlined">store</span>
                    </div>
                    <div className="kpi-info">
                      <span className="kpi-label">Tiendas Aliadas</span>
                      <span className="kpi-value text-amber-400">
                        {loadingKpis ? '...' : kpis.totalTiendas}
                      </span>
                      <span className="kpi-subtext">tabla: tiendas</span>
                    </div>
                  </div>
                </div>

                {/* Database Modules Quick Grid */}
                <div className="mt-8">
                  <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#b7f900]">dataset</span>
                    Módulos Habilitados en Base de Datos (MCP)
                  </h2>
                  <div className="admin-quick-tables-grid">
                    {visibleSidebarModules.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className="quick-table-btn"
                        onClick={() => changeTab(m.id, m.tabla_asociada)}
                      >
                        <div className="text-left">
                          <span className="table-btn-title flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-sm text-[#b7f900]">
                              {m.icono}
                            </span>
                            {m.nombre}
                          </span>
                          <span className="table-btn-category">
                            {m.tabla_asociada ? `tabla: ${m.tabla_asociada}` : 'módulo central'}
                          </span>
                        </div>
                        <span className="material-symbols-outlined text-gray-500 text-sm">arrow_forward</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* VIEW 2: USUARIOS */}
            {activeTab === 'usuarios' && (
              <div className="admin-content-view">
                <div className="admin-section-header">
                  <div>
                    <h2 className="text-xl font-bold text-white">Administración de Usuarios</h2>
                    <p className="text-xs text-gray-400">
                      Gestiona roles, información de contacto, códigos de referido y estado de cuenta.
                    </p>
                  </div>
                  <div className="admin-actions-bar">
                    <div className="admin-search-wrapper">
                      <span className="material-symbols-outlined search-icon">search</span>
                      <input
                        type="text"
                        placeholder="Buscar por usuario, email, rut o código..."
                        value={usuariosSearch}
                        onChange={(e) => setUsuariosSearch(e.target.value)}
                        className="admin-search-input"
                      />
                    </div>

                    <select
                      value={usuariosRoleFilter}
                      onChange={(e) => setUsuariosRoleFilter(e.target.value)}
                      className="admin-filter-select"
                    >
                      <option value="todos">Todos los roles</option>
                      <option value="admin">Administradores</option>
                      <option value="embajador">Embajadores</option>
                      <option value="staff">Staff Programadores</option>
                      <option value="usuario">Usuarios</option>
                      <option value="sin_rol">Sin rol asignado</option>
                    </select>
                  </div>
                </div>

                {loadingUsuarios ? (
                  <div className="text-center py-16">
                    <span className="loading loading-spinner loading-lg text-[#b7f900]"></span>
                    <p className="text-xs text-gray-400 mt-2">Cargando usuarios desde Supabase...</p>
                  </div>
                ) : (
                  <div className="admin-table-container">
                    <table className="admin-data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Usuario</th>
                          <th>Email</th>
                          <th>RUT / Celular</th>
                          <th>Código Referido</th>
                          <th>Rol Actual</th>
                          <th>Estado</th>
                          {isSuperAdmin && <th className="text-right">Permisos</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsuarios.map((u) => {
                          return (
                            <tr key={u.id}>
                              <td className="font-mono text-gray-400 text-xs">{u.id}</td>
                              <td>
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center font-bold text-xs text-white">
                                    {(u.username || u.email || 'U')[0].toUpperCase()}
                                  </div>
                                  <span className="font-semibold text-white">
                                    {u.username || 'Sin username'}
                                  </span>
                                </div>
                              </td>
                              <td className="font-mono text-xs text-gray-300">
                                <div className="flex items-center gap-1.5">
                                  <span>{u.email || '-'}</span>
                                  {u.email && (
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(u.email, `user-email-${u.id}`)}
                                      className="p-1 rounded text-gray-400 hover:text-[#b7f900] hover:bg-white/5 transition inline-flex items-center"
                                      title="Copiar email al portapapeles"
                                    >
                                      <span className="material-symbols-outlined text-sm">
                                        {copiedId === `user-email-${u.id}` ? 'done' : 'content_copy'}
                                      </span>
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="text-xs text-gray-400">
                                {u.rut || '-'}{u.celular ? ` • ${u.celular}` : ''}
                              </td>
                              <td>
                                <span className="font-mono text-xs text-[#b7f900] bg-[#b7f900]/10 px-2 py-0.5 rounded">
                                  {u.codigo_referido}
                                </span>
                              </td>
                              <td>
                                <span className={`role-pill-badge role-${u.role || 'usuario'}`}>
                                  {u.role || 'sin rol'}
                                </span>
                              </td>
                              <td>
                                <span className={`status-dot ${u.estado_online ? 'online' : 'offline'}`}>
                                  {u.estado_online ? 'Online' : 'Offline'}
                                </span>
                              </td>
                              {isSuperAdmin && (
                                <td className="text-right">
                                  <button
                                    type="button"
                                    className="btn-action-small"
                                    onClick={() => {
                                      handleSelectUserForPerms(u);
                                      changeTab('permisos');
                                    }}
                                    title="Configurar permisos de este usuario"
                                  >
                                    <span className="material-symbols-outlined text-sm">tune</span>
                                    <span>Asignar Permisos</span>
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                        {filteredUsuarios.length === 0 && (
                          <tr>
                            <td colSpan={8} className="text-center py-8 text-gray-500">
                              No se encontraron usuarios con los filtros aplicados.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* VIEW 3: PRODUCTOS */}
            {activeTab === 'productos' && (
              <div className="admin-content-view">
                <div className="admin-section-header">
                  <div>
                    <h2 className="text-xl font-bold text-white">Catálogo de Productos & Inventario</h2>
                    <p className="text-xs text-gray-400">
                      Visualiza productos, precios cliente vs proveedor, puntos asignados y alternar disponibilidad.
                    </p>
                  </div>
                  <div className="admin-actions-bar">
                    <div className="admin-search-wrapper">
                      <span className="material-symbols-outlined search-icon">search</span>
                      <input
                        type="text"
                        placeholder="Buscar por SKU o nombre de producto..."
                        value={productosSearch}
                        onChange={(e) => setProductosSearch(e.target.value)}
                        className="admin-search-input"
                      />
                    </div>

                    <select
                      value={productosDisponibilidad}
                      onChange={(e) => setProductosDisponibilidad(e.target.value)}
                      className="admin-filter-select"
                    >
                      <option value="todos">Todos los estados</option>
                      <option value="disponible">Solo Disponibles</option>
                      <option value="agotado">Solo Agotados</option>
                    </select>
                  </div>
                </div>

                {loadingProductos ? (
                  <div className="text-center py-16">
                    <span className="loading loading-spinner loading-lg text-[#b7f900]"></span>
                    <p className="text-xs text-gray-400 mt-2">Cargando catálogo desde Supabase...</p>
                  </div>
                ) : (
                  <div className="admin-table-container">
                    <table className="admin-data-table">
                      <thead>
                        <tr>
                          <th>Foto</th>
                          <th>SKU</th>
                          <th>Producto</th>
                          <th>Categoría</th>
                          <th className="text-right">Precio Cliente</th>
                          <th className="text-right">Precio Proveedor</th>
                          <th className="text-center">Puntos Sorteo</th>
                          <th className="text-center">Pts Regalo</th>
                          <th className="text-center">Disponibilidad</th>
                          <th className="text-right">Ver</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProductos.map((p) => {
                          const isUpdating = updatingSku === p.sku;
                          return (
                            <tr key={p.id}>
                              <td>
                                <div className="admin-product-thumb">
                                  <img
                                    src={p.imagen_1 || '/img/placeholder-product.png'}
                                    alt={p.producto}
                                  />
                                </div>
                              </td>
                              <td className="font-mono text-xs text-gray-300 font-semibold">{p.sku}</td>
                              <td className="max-w-xs">
                                <span className="font-semibold text-white block truncate" title={p.producto}>
                                  {p.producto}
                                </span>
                                {p.es_oficial && (
                                  <span className="text-[10px] text-[#D4AF37] font-semibold flex items-center gap-0.5">
                                    <span className="material-symbols-outlined text-[12px]">verified</span>
                                    Oficial MatiMarket
                                  </span>
                                )}
                              </td>
                              <td className="text-xs text-gray-400">
                                {p.categoria?.categoria || 'General'}
                              </td>
                              <td className="text-right font-semibold text-white font-mono text-xs">
                                ${p.precio_cliente?.toLocaleString()}
                              </td>
                              <td className="text-right text-gray-400 font-mono text-xs">
                                ${p.precio_proveedor?.toLocaleString() || '-'}
                              </td>
                              <td className="text-center font-bold text-[#b7f900] text-xs">
                                {p.puntos?.toLocaleString()} pts
                              </td>
                              <td className="text-center text-[#D4AF37] text-xs">
                                +{p.puntos_regalo || 0}
                              </td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className={`disp-toggle-btn ${p.disponible ? 'disponible' : 'agotado'}`}
                                  onClick={() => handleToggleDisponibilidad(p)}
                                  disabled={isUpdating}
                                  title="Haz clic para alternar disponibilidad"
                                >
                                  {isUpdating ? (
                                    <span className="loading loading-spinner loading-xs"></span>
                                  ) : (
                                    <span>{p.disponible ? 'Disponible' : 'Agotado'}</span>
                                  )}
                                </button>
                              </td>
                              <td className="text-right">
                                <Link
                                  to={`/productos/detalle-producto/${p.sku}/participa`}
                                  className="btn-icon-link"
                                  title="Ver en la tienda"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <span className="material-symbols-outlined text-sm">open_in_new</span>
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                        {filteredProductos.length === 0 && (
                          <tr>
                            <td colSpan={10} className="text-center py-8 text-gray-500">
                              No se encontraron productos con los filtros aplicados.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* VIEW 4: GENERAL TABLES EXPLORER & LINKED MCP TABLES */}
            {(activeTab === 'tablas' || ['sorteos', 'tickets', 'referidos', 'cuentas', 'tiendas'].includes(activeTab)) && (
              <div className="admin-content-view">
                <div className="admin-section-header flex-col sm:flex-row gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                      <span className="material-symbols-outlined text-[#b7f900]">table_chart</span>
                      Visualizador de Tablas MCP Supabase
                    </h2>
                    <p className="text-xs text-gray-400">
                      Administración directa de los registros de la base de datos de MatiMarket.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-gray-400 font-semibold">Tabla activa:</label>
                    <select
                      value={selectedTable}
                      onChange={(e) => {
                        setSelectedTable(e.target.value);
                        fetchTableData(e.target.value);
                      }}
                      className="admin-table-selector"
                    >
                      {AVAILABLE_TABLES.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.id})
                        </option>
                      ))}
                    </select>

                    {canEditCurrentTable && (
                      <button
                        type="button"
                        className="btn-add-record"
                        onClick={handleOpenAddModal}
                        title={`Añadir nuevo registro en la tabla ${selectedTable}`}
                      >
                        <span className="material-symbols-outlined text-sm">add_circle</span>
                        <span>Añadir {selectedTable === 'cuentas' ? 'Cuenta' : 'Registro'}</span>
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn-export-data"
                      onClick={exportToCSV}
                      title="Exportar a CSV"
                    >
                      <span className="material-symbols-outlined text-sm">download</span>
                      <span>CSV</span>
                    </button>
                    <button
                      type="button"
                      className="btn-export-data"
                      onClick={exportToJSON}
                      title="Exportar a JSON"
                    >
                      <span className="material-symbols-outlined text-sm">data_object</span>
                      <span>JSON</span>
                    </button>
                  </div>
                </div>

                <div className="admin-table-meta-bar">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-white">
                      Tabla: <strong className="text-[#b7f900] font-mono">{selectedTable}</strong>
                    </span>
                    <span className="text-xs text-gray-400 font-mono">
                      {processedTableData.length} registros cargados
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="admin-search-wrapper">
                      <span className="material-symbols-outlined search-icon">search</span>
                      <input
                        type="text"
                        placeholder={`Buscar en ${selectedTable}...`}
                        value={tableSearch}
                        onChange={(e) => {
                          setTableSearch(e.target.value);
                          setTablePage(1);
                        }}
                        className="admin-search-input"
                      />
                    </div>

                    <select
                      value={tablePageSize}
                      onChange={(e) => {
                        setTablePageSize(Number(e.target.value));
                        setTablePage(1);
                      }}
                      className="admin-filter-select text-xs py-1"
                    >
                      <option value={10}>10 filas</option>
                      <option value={25}>25 filas</option>
                      <option value={50}>50 filas</option>
                      <option value={100}>100 filas</option>
                    </select>
                  </div>
                </div>

                {loadingTable ? (
                  <div className="text-center py-16">
                    <span className="loading loading-spinner loading-lg text-[#b7f900]"></span>
                    <p className="text-xs text-gray-400 mt-2">Cargando registros de {selectedTable}...</p>
                  </div>
                ) : (
                  <>
                    <div className="admin-table-container">
                      <table className="admin-data-table">
                        <thead>
                          <tr>
                            <th className="w-12 text-center">#</th>
                            {tableColumns.map((col) => (
                              <th
                                key={col}
                                onClick={() => {
                                  if (sortColumn === col) {
                                    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortColumn(col);
                                    setSortDirection('asc');
                                  }
                                }}
                                className="cursor-pointer select-none hover:text-[#b7f900] transition"
                              >
                                <div className="flex items-center gap-1">
                                  <span>{col}</span>
                                  {sortColumn === col && (
                                    <span className="material-symbols-outlined text-xs">
                                      {sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                                    </span>
                                  )}
                                </div>
                              </th>
                            ))}
                            {selectedTable === 'sorteos' && (
                              <th className="text-center font-bold text-[#b7f900]">Link Oficial Permanente</th>
                            )}
                            <th className="text-right">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTableData.map((row, idx) => {
                            const rowNum = (tablePage - 1) * tablePageSize + idx + 1;
                            return (
                              <tr key={idx}>
                                <td className="text-center text-gray-500 font-mono text-xs">{rowNum}</td>
                                {tableColumns.map((col) => {
                                  const val = row[col];
                                  let renderedVal = '-';
                                  if (val !== null && val !== undefined) {
                                    if (typeof val === 'boolean') {
                                      renderedVal = val ? 'true' : 'false';
                                    } else if (typeof val === 'object') {
                                      renderedVal = JSON.stringify(val);
                                    } else {
                                      renderedVal = String(val);
                                    }
                                  }

                                  return (
                                    <td
                                      key={col}
                                      className="font-mono text-xs max-w-xs truncate"
                                      title={renderedVal}
                                    >
                                      {renderedVal}
                                    </td>
                                  );
                                })}
                                {selectedTable === 'sorteos' && (
                                  <td className="text-center">
                                    <div className="inline-flex items-center gap-1.5">
                                      <a
                                        href={`/productos/detalle-producto/${row.producto}/sorteo/${row.key}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="sorteo-official-link"
                                        title="Abrir página oficial y pública del sorteo para siempre"
                                      >
                                        <span className="material-symbols-outlined text-xs text-[#b7f900]">verified</span>
                                        <span className="sorteo-link-text">/sorteo/{row.key}</span>
                                        <span className="material-symbols-outlined text-xs text-gray-400">open_in_new</span>
                                      </a>
                                      <button
                                        type="button"
                                        className="btn-icon-action"
                                        onClick={() => copyToClipboard(`${window.location.origin}/productos/detalle-producto/${row.producto}/sorteo/${row.key}`, `sorteo-link-${row.id}`)}
                                        title="Copiar URL oficial permanente al portapapeles"
                                      >
                                        <span className="material-symbols-outlined text-sm">
                                          {copiedId === `sorteo-link-${row.id}` ? 'done' : 'content_copy'}
                                        </span>
                                      </button>
                                    </div>
                                  </td>
                                )}
                                <td className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    {canEditCurrentTable && (
                                      <button
                                        type="button"
                                        className="btn-icon-action btn-edit"
                                        onClick={() => handleOpenEditModal(row)}
                                        title="Editar este registro"
                                      >
                                        <span className="material-symbols-outlined text-sm">edit</span>
                                      </button>
                                    )}
                                    {canEditCurrentTable && (
                                      <button
                                        type="button"
                                        className="btn-icon-action btn-delete"
                                        onClick={() => handleDeleteRecord(row)}
                                        title="Eliminar este registro"
                                      >
                                        <span className="material-symbols-outlined text-sm">delete</span>
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className="btn-icon-action btn-inspect"
                                      onClick={() => setSelectedRowDetail(row)}
                                      title="Ver fila completa en JSON"
                                    >
                                      <span className="material-symbols-outlined text-sm">visibility</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {paginatedTableData.length === 0 && (
                            <tr>
                              <td
                                colSpan={tableColumns.length + (selectedTable === 'sorteos' ? 3 : 2)}
                                className="text-center py-8 text-gray-500"
                              >
                                No hay registros en la tabla {selectedTable} que coincidan con la búsqueda.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {totalTablePages > 1 && (
                      <div className="admin-pagination-bar">
                        <span className="text-xs text-gray-400">
                          Página {tablePage} de {totalTablePages}
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="btn-page-nav"
                            onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                            disabled={tablePage === 1}
                          >
                            Anterior
                          </button>
                          <button
                            type="button"
                            className="btn-page-nav"
                            onClick={() => setTablePage((p) => Math.min(totalTablePages, p + 1))}
                            disabled={tablePage === totalTablePages}
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* VIEW 5: VENTAS */}
            {activeTab === 'ventas' && (
              <div className="admin-content-view">
                <VentasAdmin />
              </div>
            )}

            {/* VIEW 6: GESTOR DE PERMISOS (Super Admin) */}
            {activeTab === 'permisos' && isSuperAdmin && (
              <div className="admin-content-view">
                <div className="admin-section-header">
                  <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                      <span className="material-symbols-outlined text-[#b7f900]">security</span>
                      Gestor de Permisos y Delegación a Staff / Embajadores
                    </h2>
                    <p className="text-xs text-gray-400">
                      Controla qué páginas del sidebar y qué tablas de Supabase puede ver y modificar cada usuario.
                    </p>
                  </div>
                </div>

                <div className="permissions-manager-grid">
                  {/* Left Column: Users Selector */}
                  <div className="permissions-users-card">
                    <div className="p-3 border-b border-gray-800">
                      <span className="text-xs font-bold text-gray-300 block mb-2 uppercase tracking-wide">
                        1. Seleccionar Usuario / Embajador
                      </span>
                      <div className="admin-search-wrapper w-full">
                        <span className="material-symbols-outlined search-icon">search</span>
                        <input
                          type="text"
                          placeholder="Filtrar por username o email..."
                          value={permSearch}
                          onChange={(e) => setPermSearch(e.target.value)}
                          className="admin-search-input w-full"
                        />
                      </div>
                    </div>

                    <div className="permissions-users-list">
                      {usuarios
                        .filter(u => {
                          const q = permSearch.toLowerCase();
                          return !q ||
                            (u.username && u.username.toLowerCase().includes(q)) ||
                            (u.email && u.email.toLowerCase().includes(q)) ||
                            (u.codigo_referido && u.codigo_referido.toLowerCase().includes(q));
                        })
                        .map(u => {
                          const isSelected = permSelectedUser?.id === u.id;
                          return (
                            <button
                              key={u.id}
                              type="button"
                              className={`user-perm-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleSelectUserForPerms(u)}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center font-bold text-xs text-white flex-shrink-0">
                                  {(u.username || u.email || 'U')[0].toUpperCase()}
                                </div>
                                <div className="text-left min-w-0">
                                  <span className="text-xs font-semibold text-white block truncate">
                                    {u.username || 'Sin username'}
                                  </span>
                                  <span className="text-[11px] text-gray-400 block truncate">
                                    {u.email || u.codigo_referido}
                                  </span>
                                </div>
                              </div>

                              <span className={`role-pill-badge role-${u.role || 'usuario'}`}>
                                {u.role || 'usuario'}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  {/* Right Column: Permission Matrix */}
                  <div className="permissions-matrix-card">
                    {!permSelectedUser ? (
                      <div className="text-center py-20 px-4">
                        <span className="material-symbols-outlined text-gray-600 text-5xl mb-2">
                          badge
                        </span>
                        <h3 className="text-base font-bold text-white">Selecciona un usuario de la lista</h3>
                        <p className="text-xs text-gray-400 max-w-sm mx-auto mt-1">
                          Haz clic sobre un usuario a la izquierda para configurar su rol y activar/desactivar sus permisos para cada elemento del sidebar.
                        </p>
                      </div>
                    ) : (
                      <div className="p-5">
                        {/* Selected User Header */}
                        <div className="flex justify-between items-start flex-wrap gap-3 pb-4 border-b border-gray-800 mb-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white">
                                {permSelectedUser.username || permSelectedUser.email}
                              </h3>
                              <span className="text-xs text-gray-400 font-mono">
                                ({permSelectedUser.codigo_referido})
                              </span>
                            </div>
                            <p className="text-xs text-gray-400">{permSelectedUser.email}</p>
                          </div>

                          {/* Role selector dropdown */}
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-300 font-semibold">Rol Asignado:</label>
                            <select
                              value={permEditRole}
                              onChange={(e) => setPermEditRole(e.target.value)}
                              className="admin-filter-select text-xs py-1.5"
                            >
                              <option value="embajador">🌟 Embajador</option>
                              <option value="staff">💻 Staff Programador</option>
                              <option value="colaborador">🤝 Colaborador</option>
                              <option value="admin">👑 Administrador</option>
                              <option value="usuario">👤 Usuario Estándar</option>
                            </select>
                          </div>
                        </div>

                        {/* Quick Presets */}
                        <div className="flex items-center gap-2 flex-wrap mb-4 bg-gray-900/60 p-2.5 rounded-xl border border-gray-800">
                          <span className="text-xs text-gray-400 font-semibold mr-1">Plantillas Rápidas:</span>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline border-amber-500 text-amber-300 hover:bg-amber-500 hover:text-black"
                            onClick={() => applyPermissionPreset('embajador')}
                          >
                            🌟 Permisos Embajador
                          </button>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline border-cyan-500 text-cyan-300 hover:bg-cyan-500 hover:text-black"
                            onClick={() => applyPermissionPreset('staff')}
                          >
                            💻 Staff Programadores
                          </button>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline border-[#b7f900] text-[#b7f900] hover:bg-[#b7f900] hover:text-black"
                            onClick={() => applyPermissionPreset('admin')}
                          >
                            👑 Acceso Total
                          </button>
                        </div>

                        {/* Module Permissions Matrix */}
                        <div className="matrix-table-wrapper mb-6">
                          <table className="matrix-table">
                            <thead>
                              <tr>
                                <th>Módulo del Sidebar</th>
                                <th>Tabla MCP Asociada</th>
                                <th className="text-center w-28">Acceso (Ver)</th>
                                <th className="text-center w-28">Editar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {modulos
                                .filter(m => m.id !== 'permisos') // don't grant permission manager itself unless admin
                                .map((mod) => {
                                  const isAllowedView = !!permMatrix[mod.id]?.puede_ver;
                                  const isAllowedEdit = !!permMatrix[mod.id]?.puede_editar;

                                  return (
                                    <tr key={mod.id}>
                                      <td>
                                        <div className="flex items-center gap-2">
                                          <span className="material-symbols-outlined text-sm text-[#b7f900]">
                                            {mod.icono}
                                          </span>
                                          <div>
                                            <span className="font-semibold text-white text-xs block">
                                              {mod.nombre}
                                            </span>
                                            <span className="text-[10px] text-gray-500">
                                              {mod.descripcion}
                                            </span>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        {mod.tabla_asociada ? (
                                          <span className="font-mono text-xs text-gray-300 bg-gray-800 px-2 py-0.5 rounded">
                                            {mod.tabla_asociada}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-gray-500">-</span>
                                        )}
                                      </td>
                                      <td className="text-center">
                                        <input
                                          type="checkbox"
                                          checked={isAllowedView}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setPermMatrix(prev => ({
                                              ...prev,
                                              [mod.id]: {
                                                ...prev[mod.id],
                                                puede_ver: checked,
                                                // if unchecking view, also uncheck edit
                                                puede_editar: checked ? (prev[mod.id]?.puede_editar || false) : false
                                              }
                                            }));
                                          }}
                                          className="checkbox checkbox-sm checkbox-success"
                                        />
                                      </td>
                                      <td className="text-center">
                                        <input
                                          type="checkbox"
                                          checked={isAllowedEdit}
                                          disabled={!isAllowedView}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setPermMatrix(prev => ({
                                              ...prev,
                                              [mod.id]: {
                                                ...prev[mod.id],
                                                puede_editar: checked
                                              }
                                            }));
                                          }}
                                          className="checkbox checkbox-sm checkbox-warning"
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>

                        {permSuccessMsg && (
                          <div className="p-3 mb-4 rounded-xl bg-emerald-950/70 border border-emerald-500/40 text-emerald-200 text-xs flex items-center gap-2">
                            <span className="material-symbols-outlined text-sm">check_circle</span>
                            <span>{permSuccessMsg}</span>
                          </div>
                        )}

                        {/* Save Button */}
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            className="btn btn-primary text-black font-bold px-6"
                            onClick={handleSavePermissions}
                            disabled={permSaving}
                          >
                            {permSaving ? (
                              <span className="loading loading-spinner loading-xs mr-2"></span>
                            ) : (
                              <span className="material-symbols-outlined text-sm mr-1">save</span>
                            )}
                            Guardar Permisos en Supabase
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* JSON Row Inspector Modal */}
        {selectedRowDetail && (
          <div
            className="admin-modal-overlay"
            onClick={() => setSelectedRowDetail(null)}
          >
            <div
              className="admin-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                <h3 className="font-bold text-white text-base">
                  Detalle de Registro • {selectedTable}
                </h3>
                <button
                  type="button"
                  className="text-gray-400 hover:text-white"
                  onClick={() => setSelectedRowDetail(null)}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <div className="admin-json-viewer">
                <pre>{JSON.stringify(selectedRowDetail, null, 2)}</pre>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost text-gray-300"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(selectedRowDetail, null, 2));
                    alert('JSON copiado al portapapeles');
                  }}
                >
                  Copiar JSON
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary text-black font-semibold"
                  onClick={() => setSelectedRowDetail(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ADD RECORD MODAL */}
        {isAddModalOpen && (
          <div
            className="admin-modal-overlay"
            onClick={() => setIsAddModalOpen(false)}
          >
            <div
              className="crud-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#b7f900]">add_circle</span>
                  <h3 className="font-bold text-white text-base">
                    Añadir {selectedTable === 'cuentas' ? 'Cuenta Bancaria' : `Registro en ${selectedTable}`}
                  </h3>
                </div>
                <button
                  type="button"
                  className="text-gray-400 hover:text-white"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleSaveNewRecord} className="flex flex-col flex-1">
                <div className="crud-form-scroll">
                  <div className="crud-form-grid">
                    {tableColumns
                      .filter((col) => col !== 'id' && col !== 'created_at')
                      .map((col) => {
                        const val = recordFormData[col];
                        const isColBool = ['estado', 'activo', 'publico', 'disponible'].includes(col);
                        const isBanco = col === 'banco';
                        const isTipoCuenta = col === 'tipo_cuenta';
                        const isTextarea = ['descripcion', 'mensaje_admin', 'mensaje'].includes(col);
                        const isDate = col.includes('fecha');
                        const isTime = col.includes('hora');

                        if (isColBool) {
                          return (
                            <div key={col} className="crud-form-field">
                              <label className="crud-label">{col.replace(/_/g, ' ')}</label>
                              <select
                                value={val === true || val === 'true' ? 'true' : 'false'}
                                onChange={(e) =>
                                  setRecordFormData((prev) => ({ ...prev, [col]: e.target.value === 'true' }))
                                }
                                className="crud-select"
                              >
                                <option value="true">✅ Activo / Visible (true)</option>
                                <option value="false">❌ Inactivo / Oculto (false)</option>
                              </select>
                            </div>
                          );
                        }

                        if (isBanco) {
                          return (
                            <div key={col} className="crud-form-field">
                              <label className="crud-label">Banco / Entidad</label>
                              <select
                                value={val || ''}
                                onChange={(e) =>
                                  setRecordFormData((prev) => ({ ...prev, [col]: e.target.value }))
                                }
                                className="crud-select"
                                required
                              >
                                <option value="">-- Seleccionar Banco --</option>
                                {CHILEAN_BANKS.map((b) => (
                                  <option key={b} value={b}>
                                    {b}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        }

                        if (isTipoCuenta) {
                          return (
                            <div key={col} className="crud-form-field">
                              <label className="crud-label">Tipo de Cuenta</label>
                              <select
                                value={val || ''}
                                onChange={(e) =>
                                  setRecordFormData((prev) => ({ ...prev, [col]: e.target.value }))
                                }
                                className="crud-select"
                                required
                              >
                                <option value="">-- Seleccionar Tipo --</option>
                                <option value="Cuenta Vista">Cuenta Vista</option>
                                <option value="Cuenta Corriente">Cuenta Corriente</option>
                                <option value="Cuenta RUT">Cuenta RUT</option>
                                <option value="Cuenta de Ahorro">Cuenta de Ahorro</option>
                                <option value="Cuenta Prepago">Cuenta Prepago</option>
                              </select>
                            </div>
                          );
                        }

                        if (isTextarea) {
                          return (
                            <div key={col} className="crud-form-field col-span-full">
                              <label className="crud-label">{col.replace(/_/g, ' ')}</label>
                              <textarea
                                value={val || ''}
                                onChange={(e) =>
                                  setRecordFormData((prev) => ({ ...prev, [col]: e.target.value }))
                                }
                                rows={3}
                                className="crud-textarea"
                                placeholder={`Ingresar ${col}...`}
                              />
                            </div>
                          );
                        }

                        return (
                          <div key={col} className="crud-form-field">
                            <label className="crud-label">{col.replace(/_/g, ' ')}</label>
                            <input
                              type={isDate ? 'date' : isTime ? 'time' : 'text'}
                              value={val !== null && val !== undefined ? val : ''}
                              onChange={(e) =>
                                setRecordFormData((prev) => ({ ...prev, [col]: e.target.value }))
                              }
                              className="crud-input"
                              placeholder={`Valor para ${col}...`}
                              required={['rut', 'nombre', 'numero', 'email', 'producto', 'key'].includes(col)}
                            />
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div className="crud-actions">
                  <button
                    type="button"
                    className="btn-crud-cancel"
                    onClick={() => setIsAddModalOpen(false)}
                    disabled={savingRecord}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-crud-save"
                    disabled={savingRecord}
                  >
                    <span className="material-symbols-outlined text-sm">save</span>
                    <span>{savingRecord ? 'Guardando...' : `Guardar ${selectedTable === 'cuentas' ? 'Cuenta' : 'Registro'}`}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT RECORD MODAL */}
        {isEditModalOpen && currentEditingRow && (
          <div
            className="admin-modal-overlay"
            onClick={() => setIsEditModalOpen(false)}
          >
            <div
              className="crud-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-blue-400">edit</span>
                  <h3 className="font-bold text-white text-base">
                    Editar Registro #{currentEditingRow.id || 'N/A'} • {selectedTable}
                  </h3>
                </div>
                <button
                  type="button"
                  className="text-gray-400 hover:text-white"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleSaveEditRecord} className="flex flex-col flex-1">
                <div className="crud-form-scroll">
                  <div className="crud-form-grid">
                    {tableColumns
                      .filter((col) => col !== 'id' && col !== 'created_at')
                      .map((col) => {
                        const val = recordFormData[col];
                        const isColBool = ['estado', 'activo', 'publico', 'disponible'].includes(col);
                        const isBanco = col === 'banco';
                        const isTipoCuenta = col === 'tipo_cuenta';
                        const isTextarea = ['descripcion', 'mensaje_admin', 'mensaje'].includes(col);
                        const isDate = col.includes('fecha');
                        const isTime = col.includes('hora');

                        if (isColBool) {
                          return (
                            <div key={col} className="crud-form-field">
                              <label className="crud-label">{col.replace(/_/g, ' ')}</label>
                              <select
                                value={val === true || val === 'true' ? 'true' : 'false'}
                                onChange={(e) =>
                                  setRecordFormData((prev) => ({ ...prev, [col]: e.target.value === 'true' }))
                                }
                                className="crud-select"
                              >
                                <option value="true">✅ Activo / Visible (true)</option>
                                <option value="false">❌ Inactivo / Oculto (false)</option>
                              </select>
                            </div>
                          );
                        }

                        if (isBanco) {
                          return (
                            <div key={col} className="crud-form-field">
                              <label className="crud-label">Banco / Entidad</label>
                              <select
                                value={val || ''}
                                onChange={(e) =>
                                  setRecordFormData((prev) => ({ ...prev, [col]: e.target.value }))
                                }
                                className="crud-select"
                              >
                                <option value="">-- Seleccionar Banco --</option>
                                {CHILEAN_BANKS.map((b) => (
                                  <option key={b} value={b}>
                                    {b}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        }

                        if (isTipoCuenta) {
                          return (
                            <div key={col} className="crud-form-field">
                              <label className="crud-label">Tipo de Cuenta</label>
                              <select
                                value={val || ''}
                                onChange={(e) =>
                                  setRecordFormData((prev) => ({ ...prev, [col]: e.target.value }))
                                }
                                className="crud-select"
                              >
                                <option value="">-- Seleccionar Tipo --</option>
                                <option value="Cuenta Vista">Cuenta Vista</option>
                                <option value="Cuenta Corriente">Cuenta Corriente</option>
                                <option value="Cuenta RUT">Cuenta RUT</option>
                                <option value="Cuenta de Ahorro">Cuenta de Ahorro</option>
                                <option value="Cuenta Prepago">Cuenta Prepago</option>
                              </select>
                            </div>
                          );
                        }

                        if (isTextarea) {
                          return (
                            <div key={col} className="crud-form-field col-span-full">
                              <label className="crud-label">{col.replace(/_/g, ' ')}</label>
                              <textarea
                                value={val || ''}
                                onChange={(e) =>
                                  setRecordFormData((prev) => ({ ...prev, [col]: e.target.value }))
                                }
                                rows={3}
                                className="crud-textarea"
                                placeholder={`Ingresar ${col}...`}
                              />
                            </div>
                          );
                        }

                        return (
                          <div key={col} className="crud-form-field">
                            <label className="crud-label">{col.replace(/_/g, ' ')}</label>
                            <input
                              type={isDate ? 'date' : isTime ? 'time' : 'text'}
                              value={val !== null && val !== undefined ? val : ''}
                              onChange={(e) =>
                                setRecordFormData((prev) => ({ ...prev, [col]: e.target.value }))
                              }
                              className="crud-input"
                              placeholder={`Valor para ${col}...`}
                            />
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div className="crud-actions">
                  <button
                    type="button"
                    className="btn-crud-cancel"
                    onClick={() => setIsEditModalOpen(false)}
                    disabled={savingRecord}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-crud-save"
                    disabled={savingRecord}
                  >
                    <span className="material-symbols-outlined text-sm">save</span>
                    <span>{savingRecord ? 'Guardando...' : 'Guardar Cambios'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
